import {
  NEW_SYNC_HTTP_BULK_PAGE_BYTE_CAP,
  NEW_SYNC_HTTP_BULK_PAGE_ROW_CAP,
  type RuntimeCheckpointBlobV1,
  type NewSyncBootstrapArtifactResponse,
  type NewSyncBootstrapEligibilityResponse,
} from "@cord/shared"

export type ThreadSyncCatchupRequestPurpose =
  | "foreground_http_prefill"
  | "background_http_warm"

export type ThreadSyncHttpPrefillCatchupEvent = {
  contractVersion?: number
  replayEventId?: string
  id: number
  type: string
  payload: unknown
  timestamp: number
  conversationId?: string
  providerSessionId?: string
  childSessionId?: string
}

export type ThreadSyncHttpPrefillBaselineEvent = {
  contractVersion?: number
  replayEventId?: string
  baselineSeq: number
  type: string
  payload: unknown
  timestamp: number
  originSessionId?: string
  originEventId?: number
}

export type ThreadSyncHttpCatchupResponse = {
  after: number
  acceptedCursor: number
  tailId: number
  historyContinuity: "contiguous" | "broken"
  replicationContinuity: "contiguous" | "broken"
  historyToken: string | null
  events: ThreadSyncHttpPrefillCatchupEvent[]
  nextCursor: number
  hasMore: boolean
  count: number
  approxBytes: number
  baselineEvents?: ThreadSyncHttpPrefillBaselineEvent[]
  baselineNextSeq?: number
  baselineHasMore?: boolean
}

export type ThreadSyncHttpBaselinePageResponse = {
  events: ThreadSyncHttpPrefillBaselineEvent[]
  nextBaselineSeq: number
  hasMore: boolean
  count: number
  approxBytes: number
}

export type ThreadSyncBootstrapEligibilityResponse = NewSyncBootstrapEligibilityResponse
export type ThreadSyncBootstrapArtifactResponse = NewSyncBootstrapArtifactResponse

export const THREAD_SYNC_HTTP_CATCHUP_PAGE_ROW_CAP = NEW_SYNC_HTTP_BULK_PAGE_ROW_CAP
export const THREAD_SYNC_HTTP_CATCHUP_PAGE_BYTE_CAP = NEW_SYNC_HTTP_BULK_PAGE_BYTE_CAP

function getSessionApiBaseUrl(): string {
  const trimmedBaseUrl = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "")
  if (trimmedBaseUrl) {
    return trimmedBaseUrl
  }

  if (
    typeof window !== "undefined" &&
    typeof window.location?.origin === "string" &&
    window.location.origin.length > 0
  ) {
    return window.location.origin
  }

  return typeof globalThis.location?.origin === "string"
    ? globalThis.location.origin
    : "http://localhost"
}

function buildSessionCatchupPageUrl(args: {
  sessionId: string
  after: number
  maxRows?: number
  maxBytes?: number
  purpose?: ThreadSyncCatchupRequestPurpose
}): string {
  const baseUrl = getSessionApiBaseUrl()
  const url = new URL(
    `/api/sessions/${encodeURIComponent(args.sessionId)}/catchup-page`,
    baseUrl,
  )
  url.searchParams.set("after", String(Math.max(0, Math.floor(args.after))))
  url.searchParams.set(
    "maxRows",
    String(
      Math.max(
        1,
        Math.floor(args.maxRows ?? THREAD_SYNC_HTTP_CATCHUP_PAGE_ROW_CAP),
      ),
    ),
  )
  url.searchParams.set(
    "maxBytes",
    String(
      Math.max(
        1,
        Math.floor(args.maxBytes ?? THREAD_SYNC_HTTP_CATCHUP_PAGE_BYTE_CAP),
      ),
    ),
  )
  if (args.purpose) {
    url.searchParams.set("purpose", args.purpose)
  }
  return url.toString()
}

function buildSessionCatchupBaselinePageUrl(args: {
  sessionId: string
  afterBaselineSeq: number
  maxRows?: number
  maxBytes?: number
  purpose?: ThreadSyncCatchupRequestPurpose
}): string {
  const baseUrl = getSessionApiBaseUrl()
  const url = new URL(
    `/api/sessions/${encodeURIComponent(args.sessionId)}/catchup-baseline-page`,
    baseUrl,
  )
  url.searchParams.set(
    "afterBaselineSeq",
    String(Math.max(0, Math.floor(args.afterBaselineSeq))),
  )
  url.searchParams.set(
    "maxRows",
    String(
      Math.max(
        1,
        Math.floor(args.maxRows ?? THREAD_SYNC_HTTP_CATCHUP_PAGE_ROW_CAP),
      ),
    ),
  )
  url.searchParams.set(
    "maxBytes",
    String(
      Math.max(
        1,
        Math.floor(args.maxBytes ?? THREAD_SYNC_HTTP_CATCHUP_PAGE_BYTE_CAP),
      ),
    ),
  )
  if (args.purpose) {
    url.searchParams.set("purpose", args.purpose)
  }
  return url.toString()
}

function buildSessionBootstrapEligibilityUrl(args: {
  sessionId: string
}): string {
  const baseUrl = getSessionApiBaseUrl()
  const url = new URL(
    `/api/sessions/${encodeURIComponent(args.sessionId)}/bootstrap-eligibility`,
    baseUrl,
  )
  return url.toString()
}

function buildSessionBootstrapArtifactUrl(args: {
  sessionId: string
  conversationId?: string | null
}): string {
  const baseUrl = getSessionApiBaseUrl()
  const url = new URL(
    `/api/sessions/${encodeURIComponent(args.sessionId)}/bootstrap-artifact`,
    baseUrl,
  )
  if (args.conversationId) url.searchParams.set("conversationId", args.conversationId)
  return url.toString()
}

async function fetchThreadSyncCatchupJson<T>(args: {
  url: string
  signal: AbortSignal
}): Promise<T> {
  const response = await fetch(args.url, {
    cache: "no-store",
    credentials: "include",
    signal: args.signal,
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} while loading thread sync catchup`)
  }

  return await response.json() as T
}

export async function fetchThreadSyncCatchupPage(args: {
  sessionId: string
  after: number
  signal: AbortSignal
  maxRows?: number
  maxBytes?: number
  purpose?: ThreadSyncCatchupRequestPurpose
}): Promise<ThreadSyncHttpCatchupResponse> {
  return await fetchThreadSyncCatchupJson<ThreadSyncHttpCatchupResponse>({
    url: buildSessionCatchupPageUrl(args),
    signal: args.signal,
  })
}

export async function fetchThreadSyncBaselinePage(args: {
  sessionId: string
  afterBaselineSeq: number
  signal: AbortSignal
  maxRows?: number
  maxBytes?: number
  purpose?: ThreadSyncCatchupRequestPurpose
}): Promise<ThreadSyncHttpBaselinePageResponse> {
  return await fetchThreadSyncCatchupJson<ThreadSyncHttpBaselinePageResponse>({
    url: buildSessionCatchupBaselinePageUrl(args),
    signal: args.signal,
  })
}

export async function fetchThreadSyncBootstrapEligibility(args: {
  sessionId: string
  signal: AbortSignal
}): Promise<ThreadSyncBootstrapEligibilityResponse> {
  return await fetchThreadSyncCatchupJson<ThreadSyncBootstrapEligibilityResponse>({
    url: buildSessionBootstrapEligibilityUrl(args),
    signal: args.signal,
  })
}

export async function fetchThreadSyncBootstrapArtifact(args: {
  sessionId: string
  conversationId?: string | null
  signal: AbortSignal
}): Promise<ThreadSyncBootstrapArtifactResponse> {
  return await fetchThreadSyncCatchupJson<ThreadSyncBootstrapArtifactResponse>({
    url: buildSessionBootstrapArtifactUrl(args),
    signal: args.signal,
  })
}

export async function fetchThreadSyncBootstrapArtifactBlob(args: {
  blobFetchPath: string
  signal: AbortSignal
}): Promise<ArrayBuffer> {
  const baseUrl = getSessionApiBaseUrl()
  const url = new URL(args.blobFetchPath, baseUrl)
  const response = await fetch(url.toString(), {
    cache: "no-store",
    credentials: "include",
    signal: args.signal,
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} while loading thread sync bootstrap blob`)
  }
  return await response.arrayBuffer()
}

export type ThreadSyncRuntimeCheckpointBlob = RuntimeCheckpointBlobV1
